/**
 * @file infrastructure/cors/index.js
 * @description Punto de entrada para el sistema avanzado de manejo CORS
 * @version 3.0.0
 * @author Nubelytics Engineering Team
 * 
 * Este módulo implementa patrones arquitectónicos avanzados:
 * - Singleton para configuración centralizada
 * - Factory Method para instanciación centralizada
 * - Inyección de Dependencias para evitar referencias circulares
 * - Inversión de Control para mejorar testabilidad
 * - Chain of Responsibility en interceptores
 */

// Importar componentes del nuevo sistema CORS
import corsProxyConfig from './CorsProxyConfig';
import corsProxyRegistry, { initCorsProxy } from './CorsProxyRegistry';
import CorsProxyInterceptorSimple from './CorsProxyInterceptorSimple';
import corsDiagnosticTool from './CorsDiagnosticTool';

// Compatibilidad con el módulo legacy
import serviceFactory from './serviceFactory';
const corsProxyService = serviceFactory?.corsProxyService;

/**
 * Transforma una URL para usar el proxy CORS utilizando la nueva configuración centralizada
 * @param {string} url URL original
 * @param {Object} options Opciones de la petición
 * @returns {Object} URL transformada y opciones
 */
export function applyCorsProxy(url, options = {}) {
  // Salir rápido si no hay URL
  if (!url) return { url, options };
  
  try {
    // Verificar si la URL pertenece a dominios seguros usando la configuración centralizada
    if (!corsProxyConfig.shouldUseProxy(url)) {
      return { url, options };
    }
    // Verificar si es un dominio seguro que no necesita proxy
    if (!corsProxyConfig.shouldAlwaysUseProxy(url)) {
      return { url, options };
    }
    
    // Usar la nueva implementación basada en configuración centralizada
    const proxiedUrl = corsProxyConfig.generateProxyUrl(url);
    
    // Enriquecer las opciones con cabeceras de diagnóstico
    const enhancedOptions = {
      ...options,
      headers: {
        ...(options.headers || {}),
        'x-proxy-source': 'nubelytics-proxy-v3',
        'x-original-url': url
      }
    };
    
    // Añadir telemetría si está habilitada
    if (corsProxyConfig.ENABLE_TELEMETRY) {
      enhancedOptions.headers['x-proxy-telemetry'] = 'enabled';
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[CORS] Proxying URL: ${url} to ${proxiedUrl}`);
    }
    
    // Retornar URL transformada y opciones enriquecidas
    return {
      url: proxiedUrl,
      options: enhancedOptions
    };
  } catch (e) {
    console.error('[CORS] Error al aplicar proxy:', e);
    // En caso de error, devolver la URL original sin modificar
    return { url, options };
  }
}

/**
 * Registra interceptor CORS en instancias de Axios
 * @param {Object} axiosInstance Instancia Axios a interceptar
 * @param {string} [instanceName='custom'] Nombre identificativo opcional
 * @returns {Object} Objeto con método para desregistrar
 */
export function registerCorsInterceptor(axiosInstance, instanceName = 'custom') {
  return corsProxyRegistry.registerInterceptor(axiosInstance, instanceName);
}

/**
 * Inicializa el sistema CORS completo en la aplicación
 * @returns {Object} El registro CORS para manipulación avanzada
 */
export function initCorsSolution() {
  return initCorsProxy();
}

/**
 * Ejecuta diagnóstico completo de la configuración CORS
 * @returns {Promise<Array>} Resultados del diagnóstico
 */
export function runCorsDiagnostic() {
  return corsDiagnosticTool.runFullDiagnosis();
}

/**
 * Añade un dominio a la lista de forzado de proxy
 * @param {string} domain Dominio a añadir
 * @returns {boolean} true si se añadió correctamente
 */
export function addDomainToForceList(domain) {
  return corsProxyConfig.addDomainToForceList(domain);
}

/**
 * Elimina un dominio de la lista de forzado de proxy
 * @param {string} domain Dominio a eliminar
 * @returns {boolean} true si se eliminó correctamente
 */
export function removeDomainFromForceList(domain) {
  return corsProxyConfig.removeDomainFromForceList(domain);
}

/**
 * Exportar todos los componentes clave para acceso directo
 */
export {
  corsProxyConfig,
  corsProxyRegistry,
  CorsProxyInterceptorSimple,
  corsDiagnosticTool
};

/**
 * @deprecated Use registerCorsInterceptor en su lugar
 * Configura interceptor Axios para manejo automático de errores CORS
 * utilizando el interceptor proporcionado por ServiceFactory
 */
export function setupAxiosInterceptors() {
  try {
    // Importar axios directamente para asegurar que está disponible
    const axios = require('axios').default;

    // Verificar si el interceptor ya existe en serviceFactory
    if (typeof serviceFactory !== 'undefined' && serviceFactory && typeof serviceFactory.corsInterceptor !== 'undefined') {
      const interceptor = serviceFactory.corsInterceptor;
      if (interceptor && typeof interceptor.register === 'function') {
        const success = interceptor.register();
        if (success) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[CORS] Interceptores configurados mediante ServiceFactory');
          }
          return true;
        }
      }
    }
    
    // Verificar si podemos usar CorsProxyInterceptorSimple
    if (typeof CorsProxyInterceptorSimple !== 'undefined' && 
        CorsProxyInterceptorSimple && 
        typeof CorsProxyInterceptorSimple.register === 'function') {
      const result = CorsProxyInterceptorSimple.register(axios);
      if (result) {
        console.log('[CORS] Configurado interceptor simple con manejo defensivo');
        return true;
      }
    }
    
    // Implementación de respaldo en caso de que las opciones anteriores fallen
    // Map para seguimiento de reintentos por URL
    const retryAttempts = new Map();
    
    // Interceptor de respuesta para detectar errores CORS
    axios.interceptors.response.use(
      response => response, // Pasar respuestas exitosas sin modificar
      error => {
        // Manejo defensivo de error
        if (!error) return Promise.reject(error);
        
        // Verificar si es un error CORS con validación defensiva
        const errorMessage = error?.message || '';
        const isCorsError = 
          (errorMessage.includes('CORS') || 
           errorMessage.includes('Network Error') ||
           error?.code === 'ERR_NETWORK' ||
           (!error?.response && error?.request));
        
        // Validación adicional para evitar errores
        if (isCorsError && error?.config) {
          const url = error.config.url;
          if (!url) return Promise.reject(error);
          
          const attempts = retryAttempts.get(url) || 0;
          
          // Limitar reintentos para evitar bucles
          if (attempts < 2) {
            retryAttempts.set(url, attempts + 1);
            
            // Transformar URL usando proxy con manejo defensivo
            const { url: proxiedUrl, options } = applyCorsProxy(
              url, 
              { headers: error?.config?.headers || {} }
            );
            
            // Reintentar con proxy
            if (process.env.NODE_ENV === 'development') {
              console.log(`[CORS] Retrying with proxy: ${proxiedUrl}`);
            }
            
            // Crear nueva configuración con URL de proxy con validaciones
            const newConfig = {
              ...(error.config || {}),
              url: proxiedUrl,
              headers: options.headers || {}
            };
            
            return axios(newConfig);
          }
        }
        
        // Si no es error CORS o se superaron los reintentos, rechazar normalmente
        return Promise.reject(error);
      }
    );
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[CORS] Interceptores Axios configurados con implementación de respaldo');
    }
    
    return true;
  } catch (e) {
    console.error('[CORS] Error al configurar interceptores:', e);
    return false;
  }
}

// Inicializar y configurar automáticamente la infraestructura CORS cuando se importa este módulo
let isInitialized = false;

/**
 * Inicializa toda la infraestructura CORS necesaria
 * @returns {boolean} true si se inicializó correctamente
 */
export function initializeCorsInfrastructure() {
  if (isInitialized) return true;
  
  try {
    // 1. Configurar interceptores automáticamente
    const interceptorsConfigured = setupAxiosInterceptors();
    
    // 2. Inicializar diagnóstico periódico en desarrollo
    if (process.env.NODE_ENV === 'development') {
      setInterval(() => {
        try {
          // Recolectar y mostrar estadísticas de uso
          const proxyStats = corsProxyService?.stats || {};
          const interceptorStats = corsProxyRegistry?.getStats() || {};
          
          console.debug('[CORS] Estadísticas de infraestructura:', {
            proxy: {
              totalRequests: proxyStats.totalRequests || 0,
              failedRequests: proxyStats.failedRequests || 0,
              byStrategy: proxyStats.byStrategy || {}
            },
            interceptor: {
              totalRequests: interceptorStats.totalRequests || 0,
              intercepted: interceptorStats.intercepted || 0,
              recoveredErrors: interceptorStats.recoveredErrors || 0
            }
          });
        } catch (e) {
          // Ignorar errores en diagnóstico
        }
      }, 60000); // Cada minuto
    }
    
    isInitialized = true;
    return true;
  } catch (e) {
    console.error('[CORS] Error al inicializar infraestructura CORS:', e);
    return false;
  }
}

// Inicializar automáticamente
initializeCorsInfrastructure();

// Exportar utilidades para uso directo sin depender de clases
export { applyCorsProxy as transformUrl };
export { setupAxiosInterceptors as configureInterceptors };

// Exportar servicios principales - ahora a través del ServiceFactory
export { corsProxyService };
export { serviceFactory };

// Exportar toda la infraestructura para uso como módulo completo
export default {
  transformUrl: applyCorsProxy,
  configureInterceptors: setupAxiosInterceptors,
  initializeCorsInfrastructure,
  corsProxyService,
  serviceFactory
};
